import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, ArrowUpDown, Boxes, Package, AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '../lib/api';
import { PageHeader, Button, Card, Input, Select, Modal, Badge, Th, Td, EmptyState } from '../components/ui';
import { fmtMoney, fmtNum, canWrite, canDelete, SIZES } from '../lib/utils';
import { useAuth } from '../context/AuthContext';


export default function Inventory() {
  const { user } = useAuth();
  const write = canWrite(user.role);
  const [tab, setTab] = useState('raw');
  const [materials, setMaterials] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [settings, setSettings] = useState(null);

  const [matModal, setMatModal] = useState(null); // 'new' | material object
  const [prodModal, setProdModal] = useState(null);
  const [adjust, setAdjust] = useState(null); // {kind, item}
  const [rebuildOpen, setRebuildOpen] = useState(false);
  const ownerAdmin = canDelete(user.role);

  const load = () => {
    api.get('/raw-materials').then((r) => setMaterials(r.data));
    api.get('/products').then((r) => setProducts(r.data));
    api.get('/suppliers').then((r) => setSuppliers(r.data));
    api.get('/settings').then((r) => setSettings(r.data));
  };
  useEffect(() => { load(); }, []);

  const delMat = async (id) => { if (!window.confirm('Delete this material?')) return; await api.delete(`/raw-materials/${id}`); toast.success('Deleted'); load(); };
  const delProd = async (id) => { if (!window.confirm('Delete this product?')) return; await api.delete(`/products/${id}`); toast.success('Deleted'); load(); };

  return (
    <div>
      <PageHeader
        title="Inventory"
        subtitle="Raw fabric stock and finished goods"
        actions={write && (
          tab === 'raw'
            ? <Button onClick={() => setMatModal('new')} data-testid="add-material-btn"><Plus size={16} /> Raw Material</Button>
            : <div className="flex gap-2">
                {ownerAdmin && <Button variant="outline" onClick={() => setRebuildOpen(true)} data-testid="recalc-stock-btn"><RefreshCw size={16} /> Recalculate Stock</Button>}
                <Button onClick={() => setProdModal('new')} data-testid="add-product-btn"><Plus size={16} /> Product</Button>
              </div>
        )}
      />

      {rebuildOpen && <StockRebuildModal onClose={() => setRebuildOpen(false)} onDone={() => { setRebuildOpen(false); load(); }} />}

      <div className="flex gap-1 mb-4 bg-slate-100 p-1 rounded-md w-fit">
        {[['raw', 'Raw Materials', Boxes], ['products', 'Finished Goods', Package]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded transition-colors ${tab === k ? 'bg-white text-[#1E3A8A] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {tab === 'raw' ? (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <Th>Material</Th><Th>Category</Th><Th>Supplier</Th><Th right>Cost</Th>
                  <Th right>In Stock</Th><Th right>Min</Th><Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {materials.map((m) => {
                  const low = m.currentStock <= m.minStockLevel;
                  const sup = suppliers.find((s) => s.id === m.supplierId);
                  return (
                    <tr key={m.id} className="hover:bg-slate-50/60 transition-colors" data-testid="material-row">
                      <Td><span className="font-medium text-slate-800">{m.name}</span></Td>
                      <Td>{m.category}</Td>
                      <Td className="text-slate-500">{sup?.name || '—'}</Td>
                      <Td right>{fmtMoney(m.purchasePrice)}</Td>
                      <Td right>
                        <span className={low ? 'text-rose-600 font-semibold' : 'text-slate-800'}>{fmtNum(m.currentStock)} {m.unit}</span>
                        {low && <AlertTriangle size={13} className="inline ml-1 text-rose-500" />}
                      </Td>
                      <Td right className="text-slate-400">{fmtNum(m.minStockLevel)}</Td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">
                        {write && <>
                          <button onClick={() => setAdjust({ kind: 'raw', item: m })} className="text-slate-400 hover:text-[#1E3A8A] p-1" title="Adjust stock" data-testid="adjust-material-btn"><ArrowUpDown size={15} /></button>
                          <button onClick={() => setMatModal(m)} className="text-slate-400 hover:text-blue-600 p-1" title="Edit"><Pencil size={15} /></button>
                        </>}
                        {canDelete(user.role) && <button onClick={() => delMat(m.id)} className="text-slate-400 hover:text-rose-600 p-1" title="Delete"><Trash2 size={15} /></button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!materials.length && <EmptyState title="No raw materials" icon={Boxes} />}
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <Th>Product</Th><Th>SKU</Th><Th>Sizes in Stock</Th>
                  <Th right>Wholesale</Th><Th right>Retail</Th><Th right>Total Stock</Th><Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {products.map((p) => {
                  const low = p.currentStock <= 20;
                  return (
                    <tr key={p.id} className="hover:bg-slate-50/60 transition-colors" data-testid="product-row">
                      <Td>
                        <span className="font-medium text-slate-800">{p.name}</span>
                        <span className="block text-xs text-slate-400">{p.color} · {p.size}</span>
                      </Td>
                      <Td className="font-mono text-xs">{p.sku}</Td>
                      <Td>
                        <div className="flex gap-1 flex-wrap max-w-[280px]">
                          {SIZES.filter((s) => (p.sizeStock?.[s]?.currentStock ?? 0) > 0).map((s) => (
                            <span key={s} className="text-xs px-1.5 py-0.5 bg-slate-100 rounded num">{s}: {p.sizeStock[s].currentStock}</span>
                          ))}
                          {!SIZES.some((s) => (p.sizeStock?.[s]?.currentStock ?? 0) > 0) && <span className="text-xs text-slate-300">—</span>}
                        </div>
                      </Td>
                      <Td right>{fmtMoney(p.wholesalePrice)}</Td>
                      <Td right>{fmtMoney(p.retailPrice)}</Td>
                      <Td right><span className={low ? 'text-amber-600 font-semibold' : 'text-slate-800'}>{fmtNum(p.currentStock)}</span></Td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">
                        {write && <>
                          <button onClick={() => setAdjust({ kind: 'product', item: p })} className="text-slate-400 hover:text-[#1E3A8A] p-1" title="Adjust stock" data-testid="adjust-product-btn"><ArrowUpDown size={15} /></button>
                          <button onClick={() => setProdModal(p)} className="text-slate-400 hover:text-blue-600 p-1" title="Edit"><Pencil size={15} /></button>
                        </>}
                        {canDelete(user.role) && <button onClick={() => delProd(p.id)} className="text-slate-400 hover:text-rose-600 p-1" title="Delete"><Trash2 size={15} /></button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!products.length && <EmptyState title="No products" icon={Package} />}
        </Card>
      )}

      {matModal && <MaterialModal material={matModal === 'new' ? null : matModal} settings={settings} suppliers={suppliers} onClose={() => setMatModal(null)} onSaved={() => { setMatModal(null); load(); }} />}
      {prodModal && <ProductModal product={prodModal === 'new' ? null : prodModal} onClose={() => setProdModal(null)} onSaved={() => { setProdModal(null); load(); }} />}
      {adjust && <AdjustModal data={adjust} onClose={() => setAdjust(null)} onSaved={() => { setAdjust(null); load(); }} />}
    </div>
  );
}

function MaterialModal({ material, settings, suppliers, onClose, onSaved }) {
  const [f, setF] = useState(material || { name: '', category: 'Cotton', unit: 'Meters', supplierId: '', purchasePrice: 0, minStockLevel: 0, currentStock: 0 });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      if (material) await api.put(`/raw-materials/${material.id}`, f);
      else await api.post('/raw-materials', f);
      toast.success('Saved'); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal open title={material ? 'Edit Material' : 'New Raw Material'} onClose={onClose}
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-material-btn">Save</Button></>}>
      <div className="grid grid-cols-2 gap-4">
        <Input label="Name" value={f.name} onChange={(e) => set('name', e.target.value)} data-testid="material-name" />
        <Select label="Category" value={f.category} onChange={(e) => set('category', e.target.value)}>
          {(settings?.categories || []).map((c) => <option key={c}>{c}</option>)}
        </Select>
        <Select label="Unit" value={f.unit} onChange={(e) => set('unit', e.target.value)}>
          {(settings?.units || []).map((u) => <option key={u}>{u}</option>)}
        </Select>
        <Select label="Supplier" value={f.supplierId} onChange={(e) => set('supplierId', e.target.value)}>
          <option value="">— None —</option>
          {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </Select>
        <Input label="Purchase Price (KWD)" type="number" step="0.01" value={f.purchasePrice} onChange={(e) => set('purchasePrice', parseFloat(e.target.value) || 0)} />
        <Input label="Min Stock Level" type="number" value={f.minStockLevel} onChange={(e) => set('minStockLevel', parseFloat(e.target.value) || 0)} />
        {!material && <Input label="Qty (Opening Stock)" type="number" value={f.currentStock} onChange={(e) => set('currentStock', parseFloat(e.target.value) || 0)} data-testid="material-opening-qty" />}
        {material && <Input label="Qty (Current Stock)" type="number" value={f.currentStock} onChange={(e) => set('currentStock', parseFloat(e.target.value) || 0)} data-testid="material-qty-edit" />}
      </div>
    </Modal>
  );
}

function ProductModal({ product, onClose, onSaved }) {
  const [f, setF] = useState(product || { name: '', nameAr: '', design: '', color: '', size: '180x70cm', sku: '', sellingPrice: 0, wholesalePrice: 0, retailPrice: 0, openingStock: 0, openingSize: 'Universal' });
  const [sizeQty, setSizeQty] = useState(() => (product
    ? Object.fromEntries(SIZES.map((s) => [s, product.sizeStock?.[s]?.currentStock ?? 0]))
    : null));
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      if (product) await api.put(`/products/${product.id}`, { ...f, sizeQty });
      else await api.post('/products', f);
      toast.success('Saved'); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal open title={product ? 'Edit Product' : 'New Product'} onClose={onClose}
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-product-btn">Save</Button></>}>
      <div className="grid grid-cols-2 gap-4">
        <Input label="Product Name" value={f.name} onChange={(e) => set('name', e.target.value)} data-testid="product-name" />
        <Input label="Arabic Name" value={f.nameAr} onChange={(e) => set('nameAr', e.target.value)} dir="rtl" />
        <Input label="Design" value={f.design} onChange={(e) => set('design', e.target.value)} />
        <Input label="Color" value={f.color} onChange={(e) => set('color', e.target.value)} />
        <Input label="Dimensions" value={f.size} onChange={(e) => set('size', e.target.value)} />
        <Input label="SKU" value={f.sku} onChange={(e) => set('sku', e.target.value)} data-testid="product-sku" />
        <Input label="Selling Price" type="number" step="0.01" value={f.sellingPrice} onChange={(e) => set('sellingPrice', parseFloat(e.target.value) || 0)} />
        <Input label="Wholesale Price" type="number" step="0.01" value={f.wholesalePrice} onChange={(e) => set('wholesalePrice', parseFloat(e.target.value) || 0)} />
        <Input label="Retail Price" type="number" step="0.01" value={f.retailPrice} onChange={(e) => set('retailPrice', parseFloat(e.target.value) || 0)} />
        {!product && (
          <Select label="Opening Stock Size" value={f.openingSize} onChange={(e) => set('openingSize', e.target.value)} data-testid="product-opening-size">
            {SIZES.map((s) => <option key={s}>{s}</option>)}
          </Select>
        )}
        {!product && <Input label="Qty (Opening Stock)" type="number" value={f.openingStock} onChange={(e) => set('openingStock', parseFloat(e.target.value) || 0)} data-testid="product-opening-qty" />}
        {product && (
          <div className="col-span-2">
            <p className="text-xs font-medium text-slate-500 mb-2">Qty in Stock (per size)</p>
            <div className="grid grid-cols-5 gap-3">
              {SIZES.map((s) => (
                <Input key={s} label={s} type="number" value={sizeQty[s]}
                  onChange={(e) => setSizeQty((p) => ({ ...p, [s]: parseFloat(e.target.value) || 0 }))}
                  data-testid={`product-qty-${s}`} />
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

function AdjustModal({ data, onClose, onSaved }) {
  const { kind, item } = data;
  const [type, setType] = useState('stock_in');
  const [quantity, setQuantity] = useState(0);
  const [size, setSize] = useState('Universal');
  const [notes, setNotes] = useState('');
  const save = async () => {
    try {
      const url = kind === 'raw' ? `/raw-materials/${item.id}/adjust` : `/products/${item.id}/adjust`;
      await api.post(url, { quantity: Number(quantity), type, notes, size: kind === 'product' ? size : undefined });
      toast.success('Stock adjusted'); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal open title={`Adjust Stock · ${item.name}`} onClose={onClose} size="sm"
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-adjust-btn">Apply</Button></>}>
      <div className="space-y-4">
        <Select label="Movement" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="stock_in">Stock In (+)</option>
          <option value="stock_out">Stock Out (−)</option>
          <option value="adjustment">Adjustment</option>
        </Select>
        {kind === 'product' && (
          <Select label="Size" value={size} onChange={(e) => setSize(e.target.value)}>
            {SIZES.map((s) => <option key={s}>{s}</option>)}
          </Select>
        )}
        <Input label="Quantity" type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} data-testid="adjust-qty" />
        <Input label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Reason / reference" />
      </div>
    </Modal>
  );
}


function StockRebuildModal({ onClose, onDone }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get('/admin/stock-rebuild').then((r) => setData(r.data)).catch((e) => { toast.error(formatApiError(e)); onClose(); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const apply = async () => {
    if (!window.confirm('Update finished goods stock to the "New Stock" figures?')) return;
    setBusy(true);
    try {
      const r = await api.post('/admin/stock-rebuild');
      toast.success(`Stock recalculated (${r.data.changed} rows updated)`);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="Recalculate Finished Goods Stock" size="xl"
      footer={<>
        <Button variant="outline" onClick={onClose}>Close</Button>
        <Button onClick={apply} disabled={!data || busy || !data.changed} data-testid="apply-recalc-stock-btn">
          <RefreshCw size={16} /> {busy ? 'Updating…' : 'Apply New Stock'}
        </Button>
      </>}>
      <p className="text-sm text-slate-600 mb-3">
        New Stock = Opening stock + dozens from approved tailor logs (+ completed jobs) − qty sold on bills + returns.
        Products with no tailor logs are not changed. Negative results are set to 0.
      </p>
      {!data ? <p className="text-sm text-slate-500">Calculating…</p> : (
        <>
          <div className="flex gap-4 text-sm mb-3">
            <span>Current total: <b className="num">{fmtNum(data.totalCurrent)}</b></span>
            <span>New total: <b className="num text-[#1E3A8A]">{fmtNum(data.totalNew)}</b></span>
            <span>Rows changing: <b>{data.changed}</b></span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="recalc-stock-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr><Th>Product</Th><Th>Size</Th><Th right>Current</Th><Th right>Opening</Th><Th right>Tailor Dz</Th><Th right>Sold</Th><Th right>Returned</Th><Th right>New Stock</Th></tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.productId + r.size} className={`border-b border-slate-100 ${r.current !== r.newStock ? 'bg-amber-50/60' : ''}`}>
                    <Td>{r.productName}</Td><Td>{r.size}</Td>
                    <Td right className="num">{fmtNum(r.current)}</Td>
                    <Td right className="num">{fmtNum(r.opening)}</Td>
                    <Td right className="num">{fmtNum(r.made)}</Td>
                    <Td right className="num">{fmtNum(r.sold)}</Td>
                    <Td right className="num">{fmtNum(r.returned)}</Td>
                    <Td right className="num font-bold">{fmtNum(r.newStock)}{r.calculated < 0 && <span className="text-xs text-rose-600 ml-1">({fmtNum(r.calculated)})</span>}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  );
}
